import { apiConfig, getCrsApiUrl } from 'src/config/apiConfig';
import axiosIn from 'src/middlewares/axiosInterceptor';
import {
  CyberRiskAiReportStartResponse,
  CyberRiskProfile,
  CyberRiskProfileSaveRequest,
  CyberRiskProfileSaveResponse,
  CyberRiskQuestionnaireCatalog,
  CyberRiskScoreHistory,
  CyberRiskScoreResult,
} from './types';

const CRS_AI_REPORT_START_TIMEOUT_MS = 300000;

function getCrsErrorTexts(error: any): string[] {
  if (!error) return [];
  if (typeof error === 'string') return [error];
  if (Array.isArray(error)) return error.flatMap(getCrsErrorTexts);

  // AxiosError rejects with the raw error; the backend payload lives in error.response.data
  if (error.response?.data) return getCrsErrorTexts(error.response.data);

  const directTexts = [error.message, error.title, error.error].filter(Boolean);
  const mappedErrors = error.errors && typeof error.errors === 'object' ? Object.values(error.errors).flat().filter(Boolean) : [];

  return [...directTexts, ...mappedErrors].map(String);
}

export function getCrsErrorMessage(error: any) {
  return getCrsErrorTexts(error)[0] || 'CRS request failed';
}

export function isCrsError(error: any, message: string) {
  return getCrsErrorTexts(error).some((text) => text === message || text.includes(message));
}

export async function getCrsQuestionnaire(): Promise<CyberRiskQuestionnaireCatalog> {
  const response = await axiosIn.get(getCrsApiUrl(apiConfig.crs_questionnaire));
  return response.data;
}

export async function getCrsProfile(userDomainId: string): Promise<CyberRiskProfile> {
  const response = await axiosIn.get(getCrsApiUrl(apiConfig.crs_profile, { userDomainId }));
  return response.data;
}

export async function upsertCrsProfile(userDomainId: string, payload: CyberRiskProfileSaveRequest): Promise<CyberRiskProfileSaveResponse> {
  const response = await axiosIn.put(getCrsApiUrl(apiConfig.crs_profile, { userDomainId }), payload);
  return response.data;
}

export async function getLastCrsResult(userDomainId: string): Promise<CyberRiskScoreResult> {
  const response = await axiosIn.get(getCrsApiUrl(apiConfig.crs_result_last, { userDomainId }));
  return response.data;
}

export async function getCrsResult(userDomainId: string, recordId: string): Promise<CyberRiskScoreResult> {
  const response = await axiosIn.get(getCrsApiUrl(apiConfig.crs_result, { userDomainId, recordId }));
  return response.data;
}

export async function getCrsHistory(userDomainId: string): Promise<CyberRiskScoreHistory> {
  const response = await axiosIn.get(getCrsApiUrl(apiConfig.crs_history, { userDomainId }));
  return response.data;
}

export async function createCrsAiReport(userDomainId: string, recordId: string): Promise<CyberRiskAiReportStartResponse> {
  const response = await axiosIn.post(getCrsApiUrl(apiConfig.crs_ai_report, { userDomainId, recordId }), undefined, {
    timeout: CRS_AI_REPORT_START_TIMEOUT_MS,
  });
  return response.data;
}
